import {
  Alert,
  AlertTitle,
  Box,
  Button,
  ButtonProps,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Tooltip
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Device } from '../types'
import useDialog from '../hooks/useDialog'
import { usePolicy } from '../contexts/PolicyContext'
import LoadingState from './LoadingState'
import {
  FirmwareVersionUpdateTimeoutError,
  updateDeviceFirmwareVersion
} from '../services/deviceService'
import { getLatestDeviceFirmwareVersion, getLatestOtaFirmwareVersion } from '../services/ota'
import { OtaFirmwareVersionType } from '../enums'

export interface DeviceUpdateFirmwareVersionButtonProps extends Omit<ButtonProps, 'children'> {
  device: Device
  latestFirmwareVersion?: string | null
  firmwareType?: OtaFirmwareVersionType
  onDeviceChange?: (device: Device) => void
  onUpdatingChange?: (isUpdating: boolean) => void
}

export default function DeviceUpdateFirmwareVersionButton({
  device,
  latestFirmwareVersion: initialFirmwareVersion,
  firmwareType,
  onDeviceChange,
  onUpdatingChange,
  ...buttonProps
}: DeviceUpdateFirmwareVersionButtonProps): React.ReactElement | null {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The policy state.
   */
  const { canInvokeDeviceAction } = usePolicy()

  /**
   * Indicates if the user can update the firmware version.
   */
  const canUpdateFirmwareVersion = canInvokeDeviceAction(device)

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * The latest firmware version, initialized from the prop.
   */
  const [latestFirmwareVersion, setLatestFirmwareVersion] = useState<string | null>(
    initialFirmwareVersion ?? null
  )

  /**
   * The submitting state, includes waiting for the update to be verified.
   */
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  /**
   * Set the submitting state and emit the updating state.
   */
  function setSubmitting(value: boolean) {
    setIsSubmitting(value)
    onUpdatingChange?.(value)
  }

  /**
   * The verified state.
   */
  const [isVerified, setVerified] = useState<boolean>(false)

  /**
   * The error state.
   */
  const [error, setError] = useState<unknown>(null)

  /**
   * The reason of the error.
   */
  const errorReason: 'timeout' | 'failed' | null =
    error === null
      ? null
      : error instanceof FirmwareVersionUpdateTimeoutError
        ? 'timeout'
        : 'failed'

  /**
   * The abort controller for the running update.
   */
  const abortControllerRef = useRef<AbortController | null>(null)

  /**
   * Load the latest firmware version.
   */
  async function loadLatestFirmwareVersion(signal: AbortSignal) {
    try {
      setLatestFirmwareVersion(
        firmwareType !== undefined
          ? await getLatestOtaFirmwareVersion(firmwareType, { signal })
          : await getLatestDeviceFirmwareVersion(device.type, { signal })
      )
    } catch {
      // Ignore the error.
    }
  }

  /**
   * Load the latest firmware version when not provided.
   */
  useEffect(() => {
    if (!canUpdateFirmwareVersion || initialFirmwareVersion !== undefined) {
      return
    }

    const controller = new AbortController()

    loadLatestFirmwareVersion(controller.signal)

    return () => controller.abort()
  }, [canUpdateFirmwareVersion, initialFirmwareVersion, firmwareType, device.type])

  /**
   * Abort the running update on unmount.
   */
  useEffect(() => {
    return () => abortControllerRef.current?.abort()
  }, [])

  /**
   * Close the dialog and reset the state. A running update continues in the background.
   */
  function handleClose() {
    closeDialog()

    if (isSubmitting) {
      return
    }

    abortControllerRef.current = null
    setVerified(false)
    setError(null)
  }

  /**
   * Invoke the firmware update and poll until the device reports the latest firmware version.
   */
  async function handleSubmit() {
    if (!latestFirmwareVersion) {
      return
    }

    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      setSubmitting(true)
      setError(null)

      await updateDeviceFirmwareVersion(device.name, latestFirmwareVersion, {
        signal: controller.signal
      })

      setVerified(true)
      onDeviceChange?.({ ...device, firmwareVersion: latestFirmwareVersion })
    } catch (error) {
      if (!controller.signal.aborted) {
        setError(error)
      }
    } finally {
      if (!controller.signal.aborted) {
        setSubmitting(false)
      }
    }
  }

  // Render nothing when the user is not allowed, the latest firmware version is unknown or the device is up to date.
  if (
    !canUpdateFirmwareVersion ||
    !latestFirmwareVersion ||
    device.firmwareVersion === latestFirmwareVersion
  ) {
    return null
  }

  return (
    <>
      {isSubmitting ? (
        // Match the size of a small icon button.
        <Tooltip title={t('firmwareVersionUpdateInProgress')}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34
            }}
          >
            <CircularProgress size={20} />
          </Box>
        </Tooltip>
      ) : (
        <Button
          variant="outlined"
          color="warning"
          size="small"
          {...buttonProps}
          onClick={openDialog}
        >
          {t('updateFirmwareVersion')}
        </Button>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        {!isSubmitting && <DialogTitle>{t('updateFirmwareVersion')}</DialogTitle>}
        <DialogContent>
          {isVerified ? (
            <Alert severity="success">
              <AlertTitle sx={{ m: 0 }}>{t('firmwareVersionUpdateCompleted')}</AlertTitle>
            </Alert>
          ) : isSubmitting ? (
            <LoadingState title={t('firmwareVersionUpdateInProgress')} />
          ) : errorReason === 'timeout' ? (
            <Alert severity="warning">
              <AlertTitle sx={{ m: 0 }}>{t('firmwareVersionUpdateNotVerified')}</AlertTitle>
            </Alert>
          ) : errorReason === 'failed' ? (
            <Alert severity="error">
              <AlertTitle sx={{ m: 0 }}>{t('firmwareVersionUpdateFailed')}</AlertTitle>
            </Alert>
          ) : (
            <Alert severity="warning">
              <AlertTitle sx={{ m: 0 }}>
                {t('updateDeviceFirmwareVersionWarning', { version: latestFirmwareVersion })}
              </AlertTitle>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {isVerified ? (
            <Button variant="contained" color="success" onClick={handleClose}>
              {t('done')}
            </Button>
          ) : isSubmitting ? (
            <Button variant="outlined" color="primary" onClick={handleClose}>
              {t('actions:continueInBackground')}
            </Button>
          ) : (
            <>
              <Button variant="text" color="inherit" onClick={handleClose}>
                {t('actions:cancel')}
              </Button>

              <Button variant="contained" color="warning" onClick={handleSubmit}>
                {errorReason ? t('tryAgain') : t('actions:beginUpdate')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}
